import React from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
} from "@mui/material";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import { useNavigate } from "react-router-dom";
import { VM_Colors } from "../Styles/VMStyles";
import {
  formatSubscriptionGateMessage,
  getPricingRouteForGate,
  getSubscriptionGateDialogMeta,
} from "./subscriptionLifecycleHelper";

/**
 * Trial / free / premium gating modal driven by API `action`:
 * - select_plan → Choose plan (trial seat cap)
 * - upgrade → Upgrade plan (free or paid tier cap, team login on free)
 */
function SubscriptionGateDialog({
  open,
  onClose,
  onConfirm,
  message,
  action,
  plan,
  plan_tier,
  max_users,
}) {
  const navigate = useNavigate();
  const { title, confirmLabel, dismissLabel } =
    getSubscriptionGateDialogMeta(action);

  const bodyMessage = formatSubscriptionGateMessage({
    message,
    action,
    plan,
    plan_tier,
    max_users,
  });

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
      return;
    }
    navigate(getPricingRouteForGate());
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="subscription-gate-dialog-title"
      aria-describedby="subscription-gate-dialog-description"
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          boxShadow: "0 12px 40px rgba(30, 58, 138, 0.18)",
        },
      }}
    >
      <DialogContent sx={{ px: 3, pt: 4, pb: 2, textAlign: "center" }}>
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: "50%",
            mx: "auto",
            mb: 2.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "rgba(30, 58, 138, 0.08)",
          }}
        >
          <GroupsOutlinedIcon
            sx={{ fontSize: 40, color: VM_Colors.primary }}
            aria-hidden
          />
        </Box>

        <Typography
          id="subscription-gate-dialog-title"
          variant="h6"
          component="h2"
          fontWeight={700}
          color={VM_Colors.primary}
          gutterBottom
        >
          {title}
        </Typography>

        <Typography
          id="subscription-gate-dialog-description"
          variant="body2"
          color="text.secondary"
          sx={{ lineHeight: 1.65, maxWidth: 340, mx: "auto" }}
        >
          {bodyMessage}
        </Typography>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 2, lineHeight: 1.5 }}
        >
          Compare plans and manage your subscription from the Subscription page.
        </Typography>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 3,
          pt: 0,
          gap: 1,
          flexDirection: { xs: "column-reverse", sm: "row" },
          justifyContent: "flex-end",
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            color: VM_Colors.primary,
            minWidth: { xs: "100%", sm: 96 },
          }}
        >
          {dismissLabel}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleConfirm}
          autoFocus
          sx={{
            minWidth: { xs: "100%", sm: 180 },
            py: 1.1,
            borderRadius: 2,
          }}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default SubscriptionGateDialog;
